import React, { useRef, useContext } from 'react';
import styled from 'styled-components/macro';
import modularScale from 'styles/helpers/modular-scale';

import { EntityContext } from 'context/entityContext';
import { TopListsContext } from 'context/topLists';

import { ReactComponent as IconRemove } from 'icons/icon-remove.svg';
import { ReactComponent as DragIcon } from 'icons/icon-drag.svg';

import { ThemeProps } from 'types/styled';
import { IDSPInfo } from '../SelectedDSPList';
import getColorSourceOfStreams from 'components/data-containers/PerformanceTrend/helpers/getColorSourceOfStreams';
import useReorderDragDrop from 'hooks/useReorderDragDrop';

export const Item = styled.div<ThemeProps<'div'>>`
  background: white;
  display: flex;
  font-weight: 600;
  padding: ${({ theme, isDropdownDesign }): string =>
    `${isDropdownDesign ? '1px' : theme.spacing.xxsmall} 0`};
  opacity: ${({ isDragging }): number => (isDragging ? 0.3 : 1)};
  align-items: center;
  ${({ isDropdownDesign }): string =>
    isDropdownDesign
      ? `
    border: 1px solid #E6E6E6;
    background: #F7F7F7;
    margin-bottom: 4px;
    height: 39px;
    border-radius: 2px;
  `
      : ''}
`;

export const Displayname = styled.span<ThemeProps<'span'>>`
  font-size: ${modularScale(2)};
  flex-grow: 1;
  color: ${({ color }): string | undefined => color};
  user-select: none;
  ${({ isDropdownDesign }): string =>
    isDropdownDesign
      ? `
    font-size: 16px;
    font-weight: 500;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin-left: 3px;
  `
      : ''}
`;

export const RemoveButton = styled.button<ThemeProps<'button'>>`
  padding: ${({ theme }): string => theme.spacing.small};
  padding-left: 0;
  margin-right: ${({ theme }): string => theme.spacing.small};
  line-height: 1;
  color: ${({ color }): string => (color ? color : '#000000')};
  transition: ${({ theme }): string =>
    `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
  opacity: ${({ isRemovable }): number => (isRemovable ? 1 : 0.1)};
`;

export const DragHandler = styled.div<ThemeProps<'div'>>`
  padding: ${({ theme }): string => theme.spacing.xsmall};
  cursor: move;
  line-height: 1;
`;

interface IDSPItemProps {
  changeDSPOrder: (oldIndex: number, newIndex: number) => void;
  /** The item data of the item that is displayed */
  dspItem: IDSPInfo;
  /** Method to toggle the active state of a DSP */
  toggleDSP: Function;
  /** Can this item be removed? This is only the case when there are more than 1 items in the list. */
  isRemovable: boolean;
  /** Method that saves the current selectedDSPSs to redux */
  saveDSPs: () => void;
  /** Wether or not the user is using a touch device */
  isTouchDevice: boolean;
  isSourceOfStreams?: boolean;
  isDropdownDesign?: boolean;
  index: number;
}

const DSPItem: React.FC<IDSPItemProps> = (props) => {
  const {
    changeDSPOrder,
    saveDSPs,
    dspItem,
    toggleDSP,
    isRemovable,
    isSourceOfStreams,
    isDropdownDesign,
    index,
  } = props;

  const { dataColors, filters } = useContext(EntityContext);

  const { filters: topListsFilters } = useContext(TopListsContext);

  const ref = useRef(null);

  const { isDragging } = useReorderDragDrop(
    ref,
    dspItem,
    index,
    changeDSPOrder,
    saveDSPs
  );
  const opacity = isDragging ? 0 : 1;
  const color =
    isSourceOfStreams &&
    (topListsFilters
      ? !topListsFilters?.streaming || topListsFilters?.streaming === 1
      : !filters?.streaming || filters?.streaming === 1)
      ? getColorSourceOfStreams(dspItem.id as string)
      : dspItem?.color || dataColors[index];

  const dragBlock = (
    <div>
      <DragHandler onMouseDown={(e: any): any => e.stopPropagation()}>
        {isRemovable && <DragIcon />}
      </DragHandler>
    </div>
  );

  const removeBlock = (
    <RemoveButton
      onClick={(): void =>
        toggleDSP(
          isSourceOfStreams
            ? { id: dspItem.id, displayName: dspItem.displayName }
            : dspItem.name
        )
      }
      isRemovable={isRemovable}
      color={isDropdownDesign ? '#001247' : '#000000'}
    >
      <IconRemove />
    </RemoveButton>
  );

  return (
    <li ref={ref} style={{ opacity }}>
      <div onMouseDown={(e: any): void => e.preventDefault()}>
        <Item isDragging={isDragging} isDropdownDesign={isDropdownDesign}>
          {isDropdownDesign ? dragBlock : removeBlock}
          <Displayname
            color={isDropdownDesign ? '#001247' : color}
            isDropdownDesign={isDropdownDesign}
          >
            {dspItem.displayName}
          </Displayname>
          {isDropdownDesign ? removeBlock : dragBlock}
        </Item>
      </div>
    </li>
  );
};

export default DSPItem;
